import { z } from 'zod';

// Visitors: the browsers that use Lyrmo, by the random id of their visitor cookie, with the
// account they signed in to, if any. What each spent and the accounts it signed in to are kept
// 90 days for everyone; their visits, the 90 days of events, only for those who allowed insights.

/**
 * Which visitors the list shows: all, those who spent credits in the 90 days, those with an account
 * or without one, or those told apart.
 */
export const VISITOR_FILTERS = ['all', 'wrote', 'account', 'no-account', 'insights'] as const;
export type VisitorFilter = (typeof VISITOR_FILTERS)[number];

/** How the list is ordered: last seen first, or the most credits over 90 days first. */
export const VISITOR_SORTS = ['recent', 'credits'] as const;
export type VisitorSort = (typeof VISITOR_SORTS)[number];

export const VISITORS_PAGE_SIZE = 25;

/** The longest a visitor id can be, as the visitor cookie keeps it. */
export const VISITOR_ID_MAX = 64;

const count = z.int().nonnegative();

export const VisitorListQuerySchema = z.object({
  query: z
    .string()
    .trim()
    .max(254)
    .default('')
    .describe('The start of a visitor id, or part of the email of an account it signed in to.'),
  filter: z.enum(VISITOR_FILTERS).default('all'),
  sort: z.enum(VISITOR_SORTS).default('recent'),
  page: z.coerce.number().pipe(z.int().positive().max(100_000)).default(1),
});
/** What the list is asked for; each part left out takes its default. */
export interface VisitorListQuery {
  readonly query?: string;
  readonly filter?: VisitorFilter;
  readonly sort?: VisitorSort;
  readonly page?: number;
}

/** An account a browser signed in to. */
export const VisitorAccountSchema = z.object({ id: z.string(), email: z.string() });
export type VisitorAccount = z.infer<typeof VisitorAccountSchema>;

export const VisitorSummarySchema = z.object({
  visitor: z.string().describe('The random id of the visitor cookie.'),
  account: VisitorAccountSchema.nullable().describe(
    'The account it signed in to last; null if it never signed in.',
  ),
  firstDay: z.iso
    .date()
    .describe('The first UTC day it spent credits, signed in, or, if told apart, visited.'),
  lastDay: z.iso
    .date()
    .describe('The latest UTC day it spent credits, signed in, or, if told apart, visited.'),
  insights: z.boolean().describe('Whether it allowed insights, so its visits are told apart.'),
  firstSeenAt: z.iso
    .datetime()
    .nullable()
    .describe('Its first event in the 90 days kept; null unless it allowed insights.'),
  lastSeenAt: z.iso
    .datetime()
    .nullable()
    .describe('Its latest event; null unless it allowed insights.'),
  visits: count.describe('Visits and desktop app starts in the 90 days kept; 0 unless told apart.'),
  country: z.string().describe('Where its latest visit was from; empty when unknown.'),
  device: z
    .string()
    .describe('What its latest visit was on, as visits count it; empty when unknown.'),
  os: z.string(),
  browser: z.string(),
  today: count.describe('Credits spent in it today, signed in or not.'),
  week: count.describe('Credits spent in it over the last 7 days, today included.'),
  year: count.describe('Credits spent in it over the last 90 days, today included.'),
  requests: count.describe(
    'Requests to the model it sent over the 30 days the request log keeps: rewrites, AI tools, Why, and Alts.',
  ),
});
export type VisitorSummary = z.infer<typeof VisitorSummarySchema>;

/**
 * A row of the list: the visitor, and today's credits of the allowance it draws from, as Studio
 * counts them: its account's, or, without one, its own and its network's.
 */
export const VisitorListRowSchema = VisitorSummarySchema.extend({
  usedToday: count.describe(
    'Credits counted against that allowance today, others’ on its network included.',
  ),
  leftToday: count.describe('Credits left of it today.'),
});
export type VisitorListRow = z.infer<typeof VisitorListRowSchema>;

export const AdminVisitorListSchema = z.object({
  visitors: z.array(VisitorListRowSchema),
  total: count.describe('Visitors that match, over every page.'),
  page: z.int().positive(),
  pageSize: z.int().positive(),
  totals: z.object({
    visitors: count.describe('Every visitor Lyrmo knows of: by credits, accounts, or insights.'),
    wroteToday: count.describe('Visitors who spent credits today.'),
    withAccount: count.describe('Visitors who signed in to an account.'),
    insights: count.describe('Visitors who allowed insights, over the 90 days of events kept.'),
  }),
  tracksVisitors: z
    .boolean()
    .describe('Whether visitors who allowed insights are told apart; when off, none are.'),
  logsRequests: z
    .boolean()
    .describe('Whether requests are logged: off until the server has a key to seal them with.'),
});
export type AdminVisitorList = z.infer<typeof AdminVisitorListSchema>;

export const VisitorDaySchema = z.object({
  day: z.iso.date(),
  credits: count.describe('Credits spent in the browser that UTC day.'),
  spent: z
    .object({
      rewrite: count,
      tool: count,
      dictation: count,
      speech: count,
      imageText: count.default(0),
    })
    .describe('What that day’s credits went on.'),
});
export type VisitorDay = z.infer<typeof VisitorDaySchema>;

/** One visitor, for their own page: the list's row, their accounts, and their credits by day. */
export const AdminVisitorSchema = VisitorSummarySchema.extend({
  accounts: z
    .array(
      VisitorAccountSchema.extend({
        firstDay: z.iso.date().describe('The first UTC day the browser was seen signed in to it.'),
        lastDay: z.iso.date().describe('The latest.'),
      }),
    )
    .describe('Every account it signed in to, the latest first.'),
  limit: count.describe('The daily credits of a browser without an account.'),
  daily: z
    .array(VisitorDaySchema)
    .describe('Each of the last 90 UTC days, oldest first, today last; none spent on most.'),
  tracksVisitors: z
    .boolean()
    .describe('Whether visitors who allowed insights are told apart; when off, none are.'),
});
export type AdminVisitor = z.infer<typeof AdminVisitorSchema>;
